import { z } from 'zod';

import type { ProductionAspect } from './production.js';

/**
 * REKLAM YERLEŞİMLERİ — prodüksiyonun "nerede yayınlanacak" ekseni
 * (Kreatif stüdyo Faz 2, 1 Eki 2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden ayrı bir tablo
 * ═══════════════════════════════════════════════════════════════════════════
 * Kullanıcı kararı (KREATIF-STUDYO-PLANI.md §4): bu bir REKLAM KREATİFİ —
 * akışın ilk sorusu boyut, hemen ardından yerleşim ve süre. Yerleşim üç
 * kararı birden veriyor ve üçü de üretimden ÖNCE bilinmek zorunda:
 *
 *   1. Varsayılan boyut ve izin verilen boyutlar — 9:16 bir Reels'e gider,
 *      16:9 bir YouTube in-stream'e. Yerleşimin kabul etmediği boyutta
 *      üretmek, kullanıcının parasını reddedilecek bir dosyaya harcamak.
 *   2. Güvenli alan — platform arayüzü (profil satırı, açıklama, CTA düğmesi,
 *      sağdaki eylem sütunu) karenin bir kısmını ÖRTÜYOR. Ürün, üst yazı ve
 *      son kart o alanın dışına kurulur; ek boyut çıktıları da bu alana göre
 *      yeniden kadrajlanır.
 *   3. Süre — `durationSeconds: null` geldiğinde AI yerleşime göre seçiyor;
 *      kredi tahmini de (rezerv) bu tablodan bir süreyle kuruluyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ GÜVENLİ ALANLAR YAKLAŞIK — yeniden ölçülecek
 * ═══════════════════════════════════════════════════════════════════════════
 * Değerler platformların yayımladığı rehberlerden (Meta Reels/Stories
 * güvenli alan rehberi, TikTok in-feed güvenli alanı, YouTube Shorts arayüzü)
 * yuvarlanmış KESİRLER — piksel ölçümü değil. Platform arayüzü cihaz ve
 * sürüme göre değişiyor; bu sayılar Faz 6'da (son kart, üst yazı) gerçek
 * ekran görüntüleri üzerinde yeniden ölçülmeli. Şüphede GENİŞ tarafta
 * durulur: güvenli alanı fazla tutmak yalnız biraz boşluk bırakır, eksik
 * tutmak CTA'nın düğmenin altında kalması demek.
 *
 * Tarayıcıya GİDEBİLİR ve gitmeli: tezgâhın "nerede yayınlanacak" seçicisi
 * boyut listesini ve önerilen süreleri buradan çiziyor. Fiyat ya da prompt
 * YOK.
 */

export const PRODUCTION_PLACEMENTS = [
  'meta_reels',
  'meta_stories',
  'meta_feed',
  'tiktok',
  'youtube_shorts',
  'youtube_instream',
  'linkedin_feed',
  'x_feed',
] as const;
export type ProductionPlacement = (typeof PRODUCTION_PLACEMENTS)[number];
export const productionPlacementSchema = z.enum(PRODUCTION_PLACEMENTS);

/**
 * Karenin platform arayüzünce örtülen payı — her kenar için 0–1 arası kesir
 * (1080×1920'de `top: 0.14` ≈ üstten 269 px). Ürün, üst yazı, logo ve CTA bu
 * payların İÇİNE girmez.
 */
export interface PlacementSafeZone {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface PlacementSpec {
  platform: 'meta' | 'tiktok' | 'youtube' | 'linkedin' | 'x';
  defaultAspect: ProductionAspect;
  /// İlk eleman her zaman `defaultAspect`.
  allowedAspects: readonly ProductionAspect[];
  /// Süre seçicisinin önerdiği değerler (`PRODUCTION_DURATIONS` alt kümesi).
  recommendedDurations: readonly number[];
  /// `durationSeconds: null` geldiğinde rezerv tahmini ve AI'nın başlangıç noktası.
  defaultDuration: number;
  safeZone: PlacementSafeZone;
  /// Son miksin entegre loudness hedefi (LUFS). Hepsi −14: platformların
  /// yayımladığı normalizasyon hedefi bu civarda ve bake-off miksi de bunu
  /// tuttu (REPORT §6.2). Farklı bir hedef ölçülürse yalnız o satır değişir.
  loudnessLufs: number;
  /// Platformun reklam olarak kabul ettiği en uzun süre (sn). Bizim üst
  /// sınırımız ayrıca `PRODUCTION_DURATIONS`ın en büyüğü (60).
  maxDurationSec: number;
}

/// Reels / Stories / TikTok dikey arayüzü: üstte profil/ses satırı, altta
/// açıklama + CTA. Meta'nın Reels rehberi %14 üst / %35 alt diyor; Stories'in
/// kendi rehberi alt için %14 diyor ama aynı dosya iki yerleşimde birden
/// koşuyor — dar olan (Reels) esas.
const VERTICAL_FEED_SAFE: PlacementSafeZone = { top: 0.14, bottom: 0.35, left: 0.06, right: 0.06 };

export const PLACEMENT_SPECS: Readonly<Record<ProductionPlacement, PlacementSpec>> = {
  meta_reels: {
    platform: 'meta',
    defaultAspect: '9:16',
    allowedAspects: ['9:16', '4:5', '1:1'],
    recommendedDurations: [6, 10, 15, 20, 30],
    defaultDuration: 15,
    safeZone: VERTICAL_FEED_SAFE,
    loudnessLufs: -14,
    maxDurationSec: 90,
  },
  meta_stories: {
    platform: 'meta',
    defaultAspect: '9:16',
    allowedAspects: ['9:16'],
    recommendedDurations: [6, 10, 15],
    defaultDuration: 10,
    safeZone: VERTICAL_FEED_SAFE,
    loudnessLufs: -14,
    maxDurationSec: 60,
  },
  /// Akış videosu: arayüz kareyi örtmüyor (yalnız alt köşede ses düğmesi) —
  /// küçük bir kenar payı yeterli.
  meta_feed: {
    platform: 'meta',
    defaultAspect: '4:5',
    allowedAspects: ['4:5', '1:1', '16:9'],
    recommendedDurations: [6, 10, 15, 20, 30],
    defaultDuration: 15,
    safeZone: { top: 0.05, bottom: 0.08, left: 0.05, right: 0.05 },
    loudnessLufs: -14,
    maxDurationSec: 240,
  },
  /// TikTok sağda dikey eylem sütunu taşıyor (beğeni, yorum, paylaş) — sağ
  /// pay bu yüzden ayrıca ~%15.
  tiktok: {
    platform: 'tiktok',
    defaultAspect: '9:16',
    allowedAspects: ['9:16', '1:1', '16:9'],
    recommendedDurations: [10, 15, 20, 30],
    defaultDuration: 20,
    safeZone: { top: 0.14, bottom: 0.35, left: 0.06, right: 0.15 },
    loudnessLufs: -14,
    maxDurationSec: 60,
  },
  /// Shorts: sağda eylem sütunu, altta kanal + başlık satırı.
  youtube_shorts: {
    platform: 'youtube',
    defaultAspect: '9:16',
    allowedAspects: ['9:16'],
    recommendedDurations: [15, 20, 30, 60],
    defaultDuration: 20,
    safeZone: { top: 0.1, bottom: 0.3, left: 0.05, right: 0.15 },
    loudnessLufs: -14,
    maxDurationSec: 60,
  },
  /// In-stream: altta reklam bilgisi + "Atla" düğmesi. 6 sn = bumper
  /// (atlanamaz), 15/20 sn atlanamaz in-stream; atlanabilir olanın üst sınırı
  /// pratikte 3 dk (YouTube önerisi).
  youtube_instream: {
    platform: 'youtube',
    defaultAspect: '16:9',
    allowedAspects: ['16:9'],
    recommendedDurations: [6, 15, 20, 30],
    defaultDuration: 15,
    safeZone: { top: 0.06, bottom: 0.15, left: 0.05, right: 0.05 },
    loudnessLufs: -14,
    maxDurationSec: 180,
  },
  linkedin_feed: {
    platform: 'linkedin',
    defaultAspect: '1:1',
    allowedAspects: ['1:1', '4:5', '16:9', '9:16'],
    recommendedDurations: [15, 30],
    defaultDuration: 15,
    safeZone: { top: 0.05, bottom: 0.08, left: 0.05, right: 0.05 },
    loudnessLufs: -14,
    maxDurationSec: 1800,
  },
  x_feed: {
    platform: 'x',
    defaultAspect: '1:1',
    allowedAspects: ['1:1', '16:9', '9:16'],
    recommendedDurations: [6, 10, 15],
    defaultDuration: 10,
    safeZone: { top: 0.05, bottom: 0.08, left: 0.05, right: 0.05 },
    loudnessLufs: -14,
    maxDurationSec: 140,
  },
};

/** Yerleşim bu boyutu kabul ediyor mu. Yerleşim yoksa her boyut serbest. */
export function placementAllowsAspect(
  placement: ProductionPlacement | null | undefined,
  aspect: ProductionAspect,
): boolean {
  if (!placement) return true;
  return PLACEMENT_SPECS[placement].allowedAspects.includes(aspect);
}

/// Boyutun KENDİ güvenli alanı (yerleşim bilinmiyor ya da o boyut yerleşimin
/// asıl boyutu değil). 9:16 büyük ihtimalle Reels/TikTok'a gider — en sıkı
/// dikey arayüz payı (TikTok'un sağ sütunu dahil); diğerleri akış payı.
const VERTICAL_ANY_SAFE: PlacementSafeZone = { top: 0.14, bottom: 0.35, left: 0.06, right: 0.15 };
const FEED_ANY_SAFE: PlacementSafeZone = { top: 0.05, bottom: 0.08, left: 0.05, right: 0.05 };

/**
 * Güvenli alan — BOYUTA UYGUN (Dalga 4, 2 Eki 2026).
 *
 * Yerleşimin payları onun ASIL boyutu (`defaultAspect`) için ölçüldü: Reels'in
 * alt %35'i dikey tam ekranın açıklama + CTA şeridi. Önceden her boyuta aynı
 * pay uygulanıyordu ve 1:1/4:5 çıktıda son kart karenin üst %60'ına
 * sıkışıyordu (L4 ölçümü; `finish-render.ts` geçici olarak etrafından
 * dolanıyordu). Kural:
 *   boyut yerleşimin asıl boyutu  → yerleşimin payı
 *   başka boyut                    → boyutun kendi payı (9:16 dikey arayüz
 *                                    payı, diğerleri akış payı)
 * Yerleşim yoksa boyutun kendi payı — 9:16'da geniş tutmak Reels/TikTok
 * ihtimaline karşı sigorta.
 */
export function safeZoneFor(
  placement: ProductionPlacement | null | undefined,
  aspect: ProductionAspect,
): PlacementSafeZone {
  if (placement && PLACEMENT_SPECS[placement].defaultAspect === aspect) {
    return PLACEMENT_SPECS[placement].safeZone;
  }
  return aspect === '9:16' ? VERTICAL_ANY_SAFE : FEED_ANY_SAFE;
}
